import React from 'react';

import { useInfiniteQuery, useMutation } from 'react-query';

import UseToast from 'hooks/use-toast';
import { GetCareerPathReviews, ReportFeedbackService } from 'services/core';

import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';
import RatingStars from '@core/components/ratingstars/RatingStars';
import ReviewCard from 'components/pages/reviews/ReviewCard';

import classes from './CareerPathReviews.module.scss';

const PAGE_SIZE = 6;

const CareerPathReviews = ({ careerPathId, rate }) => {
	const { ToastContainer, toast } = UseToast();
	const { mutate: sendFeedbackReport } = useMutation(ReportFeedbackService, {
		onSuccess: (data) => {
			toast(data.data.message);
		}
	});
	const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery(
		['career-path-reviews', careerPathId],
		({ pageParam = 1 }) =>
			GetCareerPathReviews({ id: careerPathId, page_size: PAGE_SIZE, page: pageParam }),
		{
			getNextPageParam: (lastpage, pages) => (lastpage.data?.next ? pages.length + 1 : undefined),
			enabled: !!careerPathId,
			cacheTime: 0,
			staleTime: 0
		}
	);

	const reviews = data?.pages?.flatMap((page) => page.data?.results || []) || [];

	if (isLoading) {
		return (
			<div className={`${classes['loading']} d-flex align-items-center justify-content-center`}>
				<Spinner />
			</div>
		);
	}

	if (!reviews.length) {
		return null;
	}

	return (
		<section className={classes['reviews']}>
			<ToastContainer />
			<div className={classes['header']}>
				<h3 className={classes['title']}>Learner Reviews</h3>
				{rate > 0 && (
					<div className={classes['rating']}>
						<div className={classes['rating-top']}>
							<span className={classes['rating-value']}>{Number(rate).toFixed(1)}</span>
							<RatingStars value={rate} size={18} />
						</div>
						<span className={classes['rating-label']}>User Review Stars</span>
					</div>
				)}
			</div>

			<div className={classes['grid']}>
				{reviews.map((review) => (
					<ReviewCard key={review.id} review={review} onReport={sendFeedbackReport} />
				))}
			</div>

			{hasNextPage && !isFetchingNextPage ? (
				<ActionButton kind="blank" onClick={() => fetchNextPage()}>
					More reviews...
				</ActionButton>
			) : (
				isFetchingNextPage && (
					<div className="d-flex align-items-center justify-content-center w-100 py-4">
						<Spinner />
					</div>
				)
			)}
		</section>
	);
};

export default React.memo(CareerPathReviews);
